import { badge, button, card, input, metricCard, navigation, table } from '@beautyburst/tokens';
import type { CSSProperties } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'plum' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type BadgeVariant = keyof typeof badge.variant;

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .join(' ');
}

export function buttonStyle(variant: ButtonVariant, size: ButtonSize): CSSProperties {
  const v = button[variant];
  const s = button.size[size];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    background: v.background,
    color: v.text,
    border: `1px solid ${v.border}`,
    borderRadius: v.radius,
    boxShadow: v.shadow,
    height: s.height,
    minHeight: '44px',
    padding: `0 ${s.paddingX}`,
    fontSize: variant === 'primary' ? button.primary.fontSize : s.fontSize,
    fontWeight: variant === 'primary' ? button.primary.fontWeight : button.weight,
    cursor: 'pointer',
  };
}

export function inputStyle(hasError: boolean): CSSProperties {
  return {
    width: '100%',
    background: input.background,
    color: input.text,
    border: `1px solid ${hasError ? input.borderError : input.border}`,
    borderRadius: input.radius,
    minHeight: input.height,
    padding: `0 ${input.paddingX}`,
    fontSize: '1rem',
  };
}

export function cardStyle(): CSSProperties {
  return {
    background: card.background,
    border: `1px solid ${card.border}`,
    borderRadius: card.radius,
    boxShadow: card.shadow,
    padding: card.padding,
    // A card used as a grid or flex item defaults to min-width:auto and grows
    // to its widest child, which pushed page content off screen at 390px.
    minWidth: 0,
    maxWidth: '100%',
  };
}

export function badgeStyle(variant: BadgeVariant): CSSProperties {
  const v = badge.variant[variant];
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    background: v.background,
    color: v.text,
    borderRadius: badge.radius,
    padding: `${badge.paddingY} ${badge.paddingX}`,
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1.2,
  };
}

export function tableStyles() {
  return {
    wrapper: {
      border: `1px solid ${table.rowBorder}`,
      borderRadius: table.radius,
      overflow: 'hidden',
      background: card.background,
    } satisfies CSSProperties,
    table: { width: '100%', borderCollapse: 'collapse' } as CSSProperties,
    th: {
      background: table.headerBackground,
      color: table.headerText,
      textAlign: 'left',
      fontSize: '0.8125rem',
      fontWeight: 600,
      padding: `${table.cellPaddingY} ${table.cellPaddingX}`,
    } satisfies CSSProperties,
    td: {
      borderTop: `1px solid ${table.rowBorder}`,
      padding: `${table.cellPaddingY} ${table.cellPaddingX}`,
      fontSize: '0.875rem',
    } satisfies CSSProperties,
  };
}

export function metricCardStyle(): CSSProperties {
  return {
    background: metricCard.background,
    border: `1px solid ${metricCard.border}`,
    borderRadius: metricCard.radius,
    padding: metricCard.padding,
  };
}

export function sidebarStyle(): CSSProperties {
  return {
    background: navigation.portalSidebar.background,
    color: navigation.portalSidebar.text,
    width: navigation.portalSidebar.width,
    minHeight: '100vh',
    padding: '1.5rem 1rem',
  };
}
